import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Search, Briefcase, Beaker, FolderKanban, Users, HandCoins, User, Loader2, ArrowRight, Rocket, GraduationCap, FlaskConical, Building2, Truck } from "lucide-react";

const PARTNER_TYPE_ICONS = {
  startup: Rocket,
  university: GraduationCap,
  research_center: FlaskConical,
  corporation: Building2,
  provider: Truck,
  other: Users,
};

const PARTNER_TYPE_COLORS = {
  startup: "bg-orange-500/10 text-orange-600",
  university: "bg-blue-500/10 text-blue-600",
  research_center: "bg-purple-500/10 text-purple-600",
  corporation: "bg-slate-500/10 text-slate-600",
  provider: "bg-teal-500/10 text-teal-600",
  other: "bg-primary/10 text-primary",
};

const PARTNER_TYPE_LABELS = {
  startup: "Startup",
  university: "Universidad",
  research_center: "Centro Tecnológico",
  corporation: "Corporación",
  provider: "Proveedor",
  other: "Otro",
};

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partnerTypeFilter, setPartnerTypeFilter] = useState("all");
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSearch = async (value) => {
    setQuery(value);
    setPartnerTypeFilter("all");
    if (value.length < 2) {
      setResults(null);
      return;
    }

    setLoading(true);
    try {
      const data = await api.globalSearchEnhanced(token, value);
      setResults(data);
    } catch (error) {
      console.error("Search error:", error);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (type, item) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setPartnerTypeFilter("all");
    switch (type) {
      case "initiatives":
        navigate(`/initiative/${item.id}`);
        break;
      case "projects":
        navigate(`/projects`);
        break;
      case "pilots":
        navigate(`/pilots`);
        break;
      case "partners":
        navigate(`/partners`);
        break;
      case "grant_calls":
        navigate(`/grants`);
        break;
      case "users":
        navigate(`/admin`);
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    const icons = {
      initiatives: Briefcase,
      projects: FolderKanban,
      pilots: Beaker,
      partners: Users,
      grant_calls: HandCoins,
      users: User,
    };
    return icons[type] || Briefcase;
  };

  const labels = {
    initiatives: "Iniciativas",
    projects: "Proyectos I+D",
    pilots: "Pilotos",
    partners: "Partners",
    grant_calls: "Convocatorias",
    users: "Usuarios",
  };

  const availablePartnerTypes = results?.partners?.length
    ? [...new Set(results.partners.map((p) => p.type || "other"))]
    : [];

  const displayResults = results ? {
    ...results,
    partners: partnerTypeFilter === "all"
      ? results.partners
      : (results.partners || []).filter((p) => (p.type || "other") === partnerTypeFilter),
  } : null;

  const totalResults = displayResults
    ? Object.entries(displayResults)
        .filter(([key, value]) => Array.isArray(value))
        .reduce((acc, [key, arr]) => acc + arr.length, 0)
    : 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-muted/50 hover:bg-muted rounded-lg border border-border/50 transition-colors"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">Buscar iniciativas, proyectos, partners...</span>
        <span className="sm:hidden">Buscar...</span>
        <kbd className="hidden md:inline-flex ml-auto pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-xs text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Buscar en Innovation OS..."
          value={query}
          onValueChange={handleSearch}
          data-testid="global-search-input"
        />
        {!loading && availablePartnerTypes.length > 1 && (
          <div className="flex items-center gap-1.5 px-3 py-2 border-b overflow-x-auto" data-testid="partner-type-filter-bar">
            <span className="text-xs text-muted-foreground mr-1 flex-shrink-0">Filtrar partners:</span>
            <button
              onClick={() => setPartnerTypeFilter("all")}
              className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 transition-colors ${
                partnerTypeFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"
              }`}
              data-testid="partner-type-filter-all"
            >
              Todos
            </button>
            {availablePartnerTypes.map((t) => (
              <button
                key={t}
                onClick={() => setPartnerTypeFilter(t)}
                className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 transition-colors ${
                  partnerTypeFilter === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"
                }`}
                data-testid={`partner-type-filter-${t}`}
              >
                {PARTNER_TYPE_LABELS[t] || t}
              </button>
            ))}
          </div>
        )}
        <CommandList className="max-h-[400px] overflow-y-auto">
          {loading && (
            <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Buscando...
            </div>
          )}
          
          {!loading && query.length >= 2 && totalResults === 0 && (
            <CommandEmpty>No se encontraron resultados para "{query}"</CommandEmpty>
          )}
          
          {!loading && query.length >= 2 && totalResults > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-b">
              {totalResults} resultados encontrados
            </div>
          )}
          
          {!loading && displayResults && Object.entries(displayResults).map(([type, items]) => {
            if (!Array.isArray(items) || items.length === 0) return null;
            const Icon = getIcon(type);
            
            return (
              <CommandGroup key={type} heading={labels[type] || type}>
                {items.slice(0, 5).map((item) => {
                  const isPartner = type === "partners";
                  const PartnerIcon = isPartner ? (PARTNER_TYPE_ICONS[item.type] || Users) : Icon;
                  const avatarClass = isPartner
                    ? (PARTNER_TYPE_COLORS[item.type] || PARTNER_TYPE_COLORS.other)
                    : "bg-primary/10 text-primary";
                  return (
                  <CommandItem
                    key={item.id}
                    value={`${type}-${item.id}-${item.name || item.full_name}`}
                    onSelect={() => handleSelect(type, item)}
                    className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-muted/80"
                    data-testid={`search-result-${type}-${item.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${avatarClass}`}>
                      <PartnerIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {item.name || item.full_name}
                      </p>
                      {(item.description || item.sector || item.email || item.area) && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {item.description?.substring(0, 60) || item.sector || item.email || item.area}
                          {item.description && item.description.length > 60 ? "..." : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isPartner && item.type && (
                        <Badge variant="outline" className="text-xs">
                          {PARTNER_TYPE_LABELS[item.type] || item.type}
                        </Badge>
                      )}
                      {!isPartner && item.type && (
                        <Badge variant="outline" className="text-xs">
                          {item.type === "rd" ? "I+D" : item.type === "digital" ? "Digital" : item.type}
                        </Badge>
                      )}
                      {item.status && (
                        <Badge 
                          variant={item.status === "completed" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {item.status === "in_progress" ? "En curso" : 
                           item.status === "completed" ? "Completado" : 
                           item.status === "pilot" ? "Piloto" : item.status}
                        </Badge>
                      )}
                      {item.role && (
                        <Badge variant="secondary" className="text-xs">
                          {item.role}
                        </Badge>
                      )}
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CommandItem>
                  );
                })}
                {items.length > 5 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                    +{items.length - 5} más en {labels[type]}
                  </div>
                )}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
    </>
  );
};
